import { useEffect, useState } from "react";
import TextBundle from "aldoo-ra/CMS/text-bundle";
import Modal from "aldoo-ra/Modal";
import useIsMobile from "aldoo-ra/Hooks/use-is-mobile";
import DropdownSelect from "aldoo-ra/DropdownSelect";
import { sendInvites, orgInviteLink, listTeams } from "./api";


// The two themes the dialog is opened in. Both sets are written out in full:
// Tailwind generates the classes it finds in the source, so a name pieced
// together at runtime would never reach the stylesheet.
const THEMES = {
  default: {
    modal: { centered: "centered", fullScreen: "fullScreen" },
    text: "text-text",
    dim: "text-text_secondary",
    hoverText: "hover:text-text",
    border: "border-bordered",
    field: "bg-canvas",
    surface: "bg-paper",
    hover: "hover:bg-neutral_1",
    primary: "text-primary",
    primaryTint: "bg-primary/15",
    primaryTintHover: "hover:bg-primary/25",
    primaryDeep: "hover:text-primary_dk",
    button: "bg-primary hover:bg-primary_dk text-text_inv",
    focusWithin: "focus-within:ring-primary focus-within:border-primary",
    focus: "focus:ring-primary focus:border-primary",
    placeholder: "placeholder-text_secondary",
    success: "text-success",
    successTint: "bg-success/15",
    error: "text-error",
    // the buttons' and the fields' shape; the secondary button's colours
    buttonShape: "py-2.5 rounded-xl font-medium",
    secondary: "border-bordered text-text hover:bg-neutral_1",
    fieldShape: "rounded-xl",
  },
  admin: {
    modal: { centered: "admin_centered", fullScreen: "admin_fullScreen" },
    text: "text-admin_text",
    dim: "text-admin_text_secondary",
    hoverText: "hover:text-admin_text",
    border: "border-admin_bordered",
    // white with a border, as the admin's inputs
    field: "bg-admin_paper",
    surface: "bg-admin_paper",
    hover: "hover:bg-admin_neutral_1",
    primary: "text-admin_primary",
    primaryTint: "bg-admin_primary/15",
    primaryTintHover: "hover:bg-admin_primary/25",
    primaryDeep: "hover:text-admin_primary_dk",
    button: "bg-admin_primary hover:bg-admin_primary_dk text-admin_text_inv",
    focusWithin: "focus-within:ring-admin_primary focus-within:border-admin_primary",
    focus: "focus:ring-admin_primary focus:border-admin_primary",
    placeholder: "placeholder-admin_text_secondary",
    success: "text-admin_success",
    successTint: "bg-admin_success/15",
    error: "text-admin_error",
    // the admin's own buttons (Button admin_primary / admin_secondary) and fields
    buttonShape: "h-[40px] py-2 rounded-full font-bold whitespace-nowrap",
    secondary: "border-admin_primary text-admin_primary hover:bg-admin_primary hover:text-white",
    fieldShape: "rounded-md",
  },
};

/**
 * Slack-style "Invite people" dialog:
 *   - email chips input
 *   - "Add to teams (optional)": suggested chips + search over the org's
 *     teams; invitees land in the selected teams as members
 *   - Copy Invite Link (reusable share link carrying the same teams)
 *
 * `showTeams={false}` leaves the teams out — for a product whose
 * organizations have no teams, where the section is an empty search box and
 * a question the inviter cannot answer. The invite then carries no teams,
 * which is what an invite with none selected carries anyway.
 *
 * `variant="admin"` dresses it in the admin theme: the dialog opens inside
 * the admin, where the site's own colours are the wrong ones.
 *
 * `roles`: the organization's roles ({ key, name, description }) to hand
 * the invitees — one, picked from a dropdown, carried by the invite (and
 * the link) and held from the day they join. Left out, the dialog does not ask.
 */
export default function InviteUsersDialog({ orgId, onClose, showTeams = true, variant = "default", roles = null }) {
  const [inputValue, setInputValue] = useState("");
  const [emails, setEmails] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [teams, setTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]); // team objects
  const [teamSearch, setTeamSearch] = useState("");

  const [selectedRole, setSelectedRole] = useState(null); // a role's key
  const roleOptions = (roles || []).map((r) => ({ value: r.key, name: r.name, description: r.description }));
  const selectedRoles = selectedRole ? [selectedRole] : [];

  const [copied, setCopied] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);

  const texts = TextBundle({ id: "aldoo_ra_organizations" });
  const theme = THEMES[variant] || THEMES.default;

  useEffect(() => {
    if (!showTeams) return;
    listTeams(orgId)
      .then((res) => setTeams(res?.teams || []))
      .catch(() => setTeams([]));
  }, [orgId, showTeams]);

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function addFromInput() {
    const parts = inputValue
      .split(/[,;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(isValidEmail);
    if (!parts.length) return;
    setEmails((prev) => [...new Set([...prev, ...parts])]);
    setInputValue("");
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addFromInput();
    }
    if (e.key === "Backspace" && !inputValue) {
      setEmails((prev) => prev.slice(0, -1));
    }
  }

  function removeEmail(email) {
    setEmails((prev) => prev.filter((e) => e !== email));
  }

  const selectedIds = new Set(selectedTeams.map((t) => String(t._id)));
  const toggleTeam = (team) => {
    setSelectedTeams((prev) =>
      prev.find((t) => String(t._id) === String(team._id))
        ? prev.filter((t) => String(t._id) !== String(team._id))
        : [...prev, team]
    );
  };

  // Suggested: first few unselected teams; search filters the whole list.
  const q = teamSearch.trim().toLowerCase();
  const searchMatches = q
    ? teams.filter((t) => !selectedIds.has(String(t._id)) && String(t.name || "").toLowerCase().includes(q)).slice(0, 6)
    : [];
  const suggested = teams.filter((t) => !selectedIds.has(String(t._id))).slice(0, 3);

  async function handleSend() {
    const pendingInput = inputValue
      .split(/[,;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(isValidEmail);
    const batch = [...new Set([...emails, ...pendingInput])];
    if (!batch.length) return;
    setSending(true);
    setError(null);
    try {
      const res = await sendInvites(orgId, batch, selectedTeams.map((t) => t._id), selectedRoles);
      if (res.error) {
        setError(res.error);
        return;
      }
      setResult(res);
      setEmails([]);
      setInputValue("");
    } catch {
      setError(texts?.send_failed);
    } finally {
      setSending(false);
    }
  }

  async function copyInviteLink() {
    if (linkBusy) return;
    setLinkBusy(true);
    try {
      const res = await orgInviteLink(orgId, { teamIds: selectedTeams.map((t) => t._id), roles: selectedRoles });
      if (res?.url) {
        await navigator.clipboard.writeText(res.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        setError(res?.error || texts?.send_failed);
      }
    } catch {
      setError(texts?.send_failed);
    } finally {
      setLinkBusy(false);
    }
  }

  // Phones get the full-screen sheet (header + X aligned, no dead space);
  // larger screens the centered card. Hook before the early return.
  const isMobile = useIsMobile();

  if (!texts) return null;

  const teamChip = (team, selected) => (
    <button
      key={team._id}
      onClick={() => toggleTeam(team)}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
        selected
          ? `${theme.primaryTint} ${theme.primary} ${theme.primaryTintHover}`
          : `${theme.field} border ${theme.border} ${theme.dim} ${theme.hoverText} ${theme.hover}`
      }`}
    >
      <span className="font-bold">{selected ? "×" : "+"}</span>
      {team.name}
    </button>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      variant={isMobile ? theme.modal.fullScreen : theme.modal.centered}
      className={isMobile ? "" : "max-w-lg"}
      title={texts?.invite_people_title}
    >
      {result ? (
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-3 py-2">
            <div className={`w-12 h-12 rounded-full ${theme.successTint} flex items-center justify-center`}>
              <svg
                className={`w-6 h-6 ${theme.success}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            {result.sent?.length > 0 && (
              <div className="text-center">
                <p className={`text-sm font-medium ${theme.text}`}>
                  {result.sent.length === 1
                    ? texts?.invitation_sent_single
                    : `${result.sent.length} ${texts?.invitations_sent_plural}`}
                </p>
                <p className={`text-xs ${theme.dim} mt-1`}>
                  {result.sent.join(", ")}
                </p>
              </div>
            )}
            {result.skipped?.length > 0 && (
              <p className={`text-xs ${theme.dim} text-center`}>
                {result.skipped.length}{" "}
                {result.skipped.length === 1 ? texts?.address : texts?.addresses}{" "}
                {texts?.skipped_suffix}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`flex-1 ${theme.buttonShape} border ${theme.secondary} text-sm transition-colors`}
            >
              {texts?.done}
            </button>
            <button
              onClick={() => {
                setResult(null);
                setInputValue("");
              }}
              className={`flex-1 ${theme.buttonShape} ${theme.button} text-sm transition-colors`}
            >
              {texts?.invite_more}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Email chips input */}
          <div>
            <p className={`text-sm font-semibold ${theme.text} mb-1.5`}>{texts?.email_addresses || "Email addresses"}</p>
            <div
              className={`flex flex-wrap items-center gap-1.5 px-3 py-2 ${theme.fieldShape} border ${theme.border} ${theme.field} min-h-[74px] content-start cursor-text focus-within:ring-2 ${theme.focusWithin}`}
              onClick={(e) => e.currentTarget.querySelector("input")?.focus()}
            >
              {emails.map((email) => (
                <span
                  key={email}
                  className={`flex items-center gap-1 ${theme.primaryTint} ${theme.primary} text-xs px-2.5 py-1 rounded-full`}
                >
                  {email}
                  <button
                    onClick={() => removeEmail(email)}
                    className={`${theme.primaryDeep} ml-0.5`}
                  >
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={addFromInput}
                placeholder={emails.length ? "" : "name@example.com, other@example.com"}
                className={`flex-1 min-w-[160px] bg-transparent text-sm ${theme.text} ${theme.placeholder} outline-none py-1`}
              />
            </div>
          </div>

          {/* Add to teams */}
          {showTeams && (
            <div className="space-y-2">
              <p className={`text-sm font-semibold ${theme.text}`}>
                {texts?.add_to_teams || "Add to teams"}{" "}
                <span className={`font-normal ${theme.dim}`}>({texts?.optional || "optional"})</span>
              </p>
              {(selectedTeams.length > 0 || suggested.length > 0) && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {selectedTeams.map((t) => teamChip(t, true))}
                  {suggested.length > 0 && (
                    <>
                      <span className={`text-xs ${theme.dim}`}>{texts?.suggested || "Suggested:"}</span>
                      {suggested.map((t) => teamChip(t, false))}
                    </>
                  )}
                </div>
              )}
              <div className="relative">
                <input
                  type="text"
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  placeholder={texts?.search_teams || "Search teams"}
                  className={`w-full px-3 py-2 ${theme.fieldShape} border ${theme.border} ${theme.field} text-sm ${theme.text} ${theme.placeholder} focus:outline-none focus:ring-2 ${theme.focus}`}
                />
                {searchMatches.length > 0 && (
                  <div className={`absolute left-0 right-0 top-full mt-1 ${theme.surface} border ${theme.border} rounded-xl shadow-lg z-10 py-1 max-h-48 overflow-y-auto`}>
                    {searchMatches.map((t) => (
                      <button
                        key={t._id}
                        onClick={() => { toggleTeam(t); setTeamSearch(""); }}
                        className={`w-full px-3 py-1.5 text-left text-sm ${theme.text} ${theme.hover}`}
                      >
                        + {t.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Role: what the invited may do from their first day */}
          {roles?.length > 0 && (
            <div className="space-y-1.5">
              <p className={`text-sm font-semibold ${theme.text}`}>{texts?.role_label || "Role"}</p>
              <div data-testid="invite-role">
                <DropdownSelect
                  variant={variant === "admin" ? "admin" : "primary"}
                  options={roleOptions}
                  value={selectedRole ? roleOptions.filter((o) => o.value === selectedRole) : []}
                  placeholder={texts?.select_role || "Select a role"}
                  onChange={(picked) => {
                    const first = Array.isArray(picked) ? picked[0] : picked;
                    setSelectedRole(first ? first.value : null);
                  }}
                  searchEnabled
                  showCheckbox={false}
                  showSelectionTick
                  enableResetButton
                  allowEmptySelection
                  className="w-full"
                />
              </div>
              <p className={`text-xs ${theme.dim}`}>{texts?.role_hint || "What the invited may do once they join."}</p>
            </div>
          )}

          {error && <p className={`text-sm ${theme.error}`}>{String(error)}</p>}

          {/* Footer — Slack layout: link action left, Send right */}
          <div className="flex items-center gap-3">
            <button
              onClick={copyInviteLink}
              disabled={linkBusy}
              className={`inline-flex items-center gap-2 px-4 ${theme.buttonShape} border ${theme.secondary} text-sm transition-colors disabled:opacity-50`}
            >
              {copied ? (
                <svg className={`w-4 h-4 ${theme.success}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                </svg>
              )}
              {copied ? (texts?.link_copied || "Link copied") : (texts?.copy_invite_link || "Copy Invite Link")}
            </button>
            <div className="flex-1" />
            <button
              onClick={handleSend}
              disabled={sending || (emails.length === 0 && !isValidEmail(inputValue.trim()))}
              className={`px-6 ${theme.buttonShape} ${theme.button} disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors`}
            >
              {sending
                ? texts?.sending
                : emails.length > 1
                  ? texts?.send_invites
                  : texts?.send_invite || "Send"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
